// ───────────────────────────────────────────────────────────────────────────
// ESG demo data: shared helpers (reporting window, month keys, caches).
// Everything here is relative to "now" and deterministic for a given day.
// The reporting window is the last 12 calendar months (UTC), ending with the
// current month, which the core ESG series shows as a full month.
// ───────────────────────────────────────────────────────────────────────────

import {
  DAY_MS,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG_ID,
  daysInMonth,
  monthKey,
  monthStart,
  monthlyEsgSeries,
  parseQueryParam,
  round,
  startOfToday,
  type DemoCtx,
  type DemoEsgMonth,
} from '../core';

export const WINDOW_MONTHS = 12;

export const DEMO_ORG_IDS: readonly string[] = [DEMO_OUTLET_ORG_ID, DEMO_RECYCLER_ORG_ID];

export function isDemoOrgId(id: string | null | undefined): boolean {
  return id === DEMO_OUTLET_ORG_ID || id === DEMO_RECYCLER_ORG_ID;
}

export function isRecyclerOrg(id: string | null | undefined): boolean {
  return id === DEMO_RECYCLER_ORG_ID;
}

// ── Caches (cleared by resetDemoData) ─────────────────────────────────────

const resetHooks: Array<() => void> = [];

/** Register a function that clears a module's in-memory state. */
export function onReset(fn: () => void): void {
  resetHooks.push(fn);
}

export function runResetHooks(): void {
  resetHooks.forEach((fn) => fn());
}

let seriesCache = new Map<string, DemoEsgMonth[]>();
onReset(() => {
  seriesCache = new Map();
});

/** The 12-month core ESG series for a demo org (oldest first); [] for any other org. */
export function esgSeries(orgId: string): DemoEsgMonth[] {
  if (!isDemoOrgId(orgId)) return [];
  let rows = seriesCache.get(orgId);
  if (!rows) {
    rows = monthlyEsgSeries(orgId, WINDOW_MONTHS);
    seriesCache.set(orgId, rows);
  }
  return rows;
}

export function esgMonthFor(orgId: string, key: string): DemoEsgMonth | null {
  return esgSeries(orgId).find((m) => m.month === key) ?? null;
}

// ── Month keys ────────────────────────────────────────────────────────────

/** 'YYYY-MM' keys of the reporting window, oldest first. */
export function windowMonthKeys(): string[] {
  const out: string[] = [];
  for (let offset = -(WINDOW_MONTHS - 1); offset <= 0; offset += 1) out.push(monthKey(monthStart(offset)));
  return out;
}

export function currentMonthKey(): string {
  return monthKey(monthStart(0));
}

export function previousMonthKey(key: string): string {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 2, 1));
  return monthKey(d);
}

export function isInWindow(key: string): boolean {
  return windowMonthKeys().includes(key);
}

/** 00:00 UTC on the 1st of the month. */
export function monthStartDate(key: string): Date {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1));
}

/** 'YYYY-MM-01T00:00:00' (the naive month_date the API uses). */
export function monthDateNaive(key: string): string {
  return `${key}-01T00:00:00`;
}

export function monthKeyFromParts(year: number, month: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
}

/**
 * Last day (1-based) that may carry activity in a month: the full month for
 * past months, today for the current month (never in the future).
 */
export function lastActiveDay(key: string): number {
  if (key === currentMonthKey()) return startOfToday().getUTCDate();
  return daysInMonth(monthStartDate(key));
}

/** Month key requested by `?month_date=` (ISO, any time part) or `?year=&month=`; defaults to this month. */
export function monthKeyFromQuery(ctx: Pick<DemoCtx, 'query'>): string {
  const raw = parseQueryParam(ctx, 'month_date');
  if (raw && /^\d{4}-\d{2}/.test(raw)) return raw.slice(0, 7);
  const year = Number(parseQueryParam(ctx, 'year'));
  const month = Number(parseQueryParam(ctx, 'month'));
  if (Number.isInteger(year) && Number.isInteger(month) && month >= 1 && month <= 12) {
    return monthKeyFromParts(year, month);
  }
  return currentMonthKey();
}

// ── Dates ─────────────────────────────────────────────────────────────────

/** A moment inside day `day` of month `key` at `hourUtc`, clamped to at most `now - 30 min`. */
export function momentInMonth(key: string, day: number, hourUtc: number, minute = 0): Date {
  const start = monthStartDate(key);
  const d = new Date(start.getTime() + (day - 1) * DAY_MS + hourUtc * 3_600_000 + minute * 60_000);
  const latest = Date.now() - 30 * 60_000;
  return d.getTime() > latest ? new Date(Math.max(start.getTime(), latest)) : d;
}

export function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** 'YYYY-MM-DD' → Date at 00:00 UTC, or null. */
export function parseYmd(s: string | undefined | null): Date | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  const out = new Date(Date.UTC(y, m - 1, d));
  return Number.isNaN(out.getTime()) ? null : out;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function nowNaive(): string {
  return new Date().toISOString().slice(0, 19);
}

// ── Numbers ───────────────────────────────────────────────────────────────

export function sum(values: readonly number[]): number {
  let total = 0;
  for (const v of values) total += v;
  return total;
}

export function r2(v: number): number {
  return round(v, 2);
}

export function r1(v: number): number {
  return round(v, 1);
}

export function r4(v: number): number {
  return round(v, 4);
}

/** Split a full name into first / last (first word vs the rest). */
export function splitName(full: string): { first_name: string; last_name: string } {
  const parts = full.trim().split(/\s+/);
  return { first_name: parts[0] ?? '', last_name: parts.slice(1).join(' ') };
}
